<template>
  <div class="organization-details-form">
    <ContentBlock class="organization" :loading="isLoading" required>
      <div slot="instructions">
        <h3 class="f3">{{ $t('Organization Details') }}</h3>
        <p class="f5">
          <span v-if="localOrganization.type === 'developer' && !localOrganization.taxForm">{{ $t('Your organization’s information will help us determine which tax form is needed for us to pay you.') }}</span>
          <span v-else>{{ $t('Enter your organization’s information here.') }}&nbsp;</span>
          <i18n tag="span" v-if="needsAdmin" path="If you wish to update your name, business type, country, or delete your account, please contact {supportEmail}.">
            <a href="mailto:support@springboardvr.com" slot="supportEmail">support@springboardvr.com</a>
          </i18n>
        </p>
      </div>
      <div>
        <div class="organization-form">
          <UIInput
            class="name"
            v-model="localOrganization.name"
            :title="$t('Organization Name')"
            :disabled="needsAdmin"
            maxlength="255"
            required
          />
          <UISelect
            v-model="localOrganization.businessType"
            class="type"
            :title="$t('Business Type')"
            :disabled="needsAdmin"
            :placeholder="$t('Choose a Business Type')"
            required
          >
            <option value="individual">{{ $t('Individual') }}</option>
            <option value="corporation">{{ $t('Corporation') }}</option>
          </UISelect>
          <UIInput
            :title="$t('Address')"
            v-model="localOrganization.address"
            maxlength="255"
            required
          />
          <UIInput
            :title="$t('Address 2')"
            v-model="localOrganization.address2"
            maxlength="255"
          />
          <UISelect
            :title="$t('Country')"
            v-model="localOrganization.country"
            :disabled="needsAdmin"
            :placeholder="$t('Choose a Country')"
            required
          >
            <option
              v-for="country in countries"
              :value="country.countryCode"
              :key="country.countryCode"
            >{{ country.name }}</option>
          </UISelect>
          <UIInput
            :title="$t('City')"
            v-model="localOrganization.city"
            maxlength="255"
            required
          />
          <UIInput
            :title="$t('State / Province / Region')"
            v-model="localOrganization.state"
            maxlength="255"
          />
          <UIInput
            :title="$t('Postal / Zip Code')"
            v-model="localOrganization.postalCode"
            maxlength="255"
            required
          />
        </div>
      </div>
    </ContentBlock>
    <ContentBlock v-if="showContact" :loading="isLoading" required>
      <div slot="instructions">
        <h3 class="f3">{{ $t('Contact Information') }}</h3>
        <p class="f5">{{ $t('Enter your contact information here.') }}</p>
      </div>
      <div>
        <div class="organization-contact-form">
          <UIInput
            :title="$t('First Name')"
            v-model="localOrganization.contactFirstName"
            maxlength="255"
            required
          />
          <UIInput
            :title="$t('Last Name')"
            v-model="localOrganization.contactLastName"
            maxlength="255"
            required
          />
          <UIInput
            :title="$t('Your Position')"
            v-model="localOrganization.contactPosition"
            maxlength="255"
          />
          <UIInput
            type="email"
            :title="$t('Email')"
            v-model="localOrganization.contactEmail"
            maxlength="255"
            required
          />
          <UIInput
            type="tel"
            :title="$t('Phone Number')"
            v-model="localOrganization.contactPhone"
            maxlength="255"
            required
          />
        </div>
      </div>
    </ContentBlock>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import ContentBlock from '@/core/shared/components/ContentBlock';
import UISelect from '@/core/shared/components/ui/UISelect';
import UIInput from '@/core/shared/components/ui/UIInput';

@Component({
  components: {
    ContentBlock,
    UIInput,
    UISelect,
  },
  props: {
    organization: { type: Object, required: true },
    countries: { type: Array, required: true },
    showContact: { type: Boolean, default: true },
    isLoading: { type: Boolean },
  },
  watch: {
    organization: {
      handler: 'onOrganizationChanged',
      immediate: false,
      deep: false,
    },
  },
})
export default class OrganizationDetailsForm extends Vue {
  get localOrganization () {
    return new Proxy(this.organization, {
      set: (object, property, value) => {
        this.$emit('edit:organization', property, value);
        return true;
      },
    });
  }

  get needsAdmin () {
    return this.localOrganization.type === 'developer' && !!this.localOrganization.taxForm;
  }

  created () {
    this.onOrganizationChanged();
  }

  onOrganizationChanged () {
    let test = this.localOrganization.name && this.localOrganization.name.length > 0 &&
    this.localOrganization.address && this.localOrganization.address.length > 0 &&
    this.localOrganization.country &&
    this.localOrganization.city && this.localOrganization.city.length > 0 &&
    this.localOrganization.postalCode && this.localOrganization.postalCode.length > 0 &&
    !!this.localOrganization.businessType;
    if (this.showContact) {
      test = test &&
        this.localOrganization.contactFirstName && this.localOrganization.contactFirstName.length > 0 &&
        this.localOrganization.contactLastName && this.localOrganization.contactLastName.length > 0 &&
        this.localOrganization.contactEmail && this.localOrganization.contactEmail.length > 0 &&
        this.localOrganization.contactPhone && this.localOrganization.contactPhone.length > 0;
    }
    this.$emit('update:valid', test);
  }
}
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.sign-up-organization {
  margin-top: var(--spacingMd);
}

.organization-form {
  display: grid;
  grid-gap: var(--spacingMd);

  @media (--tablet) {
    grid-template-columns: repeat(6, 1fr);

    & > * {
      grid-column: span 2;
    }

    & .name {
      grid-column: 1 / 4;
    }

    & .type {
      grid-column: 4 / 7;
    }
  }
}

.organization-contact-form {
  display: grid;
  grid-gap: var(--spacingMd);

  @media (--tablet) {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
